// ============================================================
// OliviumIssueRoute — container for the Olivium issuance wizard.
//   /square/command-center/olivium/secure-access/olivium-issue
//
// Performs the REAL issue through the canonical Olivium proxy
// (api.access.create → ss-invite-create). Dry-run by default; a live issue fails
// closed to PROVIDER_REQUIRED when the engine is unconfigured — never a local
// substitute. The one-time code is held in memory only (never browser storage)
// and shown once. Idempotency key is generated once per wizard session.
//
// project_id arrives from the originating project context (?project_id=). Without
// it the wizard prompts honestly rather than inventing a project.
// ============================================================

import { COMMAND_CENTER_ROOT } from '../../commandCenterRoutes'
import { useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, ApiError, isAuthError, isForbiddenError } from '../../../../api/client'
import { SECURE_ACCESS_ROOT } from './secureAccessData'
import IssueSecureAccessWizard, { type IssueWizardResult } from './IssueSecureAccessWizard'
import { buildCreatePayload, PROVIDER_REQUIRED_CODES, type WizardForm, type WizardStatus } from './issueWizardModel'

const V2_ROOT = COMMAND_CENTER_ROOT

export default function OliviumIssueRoute() {
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const projectId = (search.get('project_id') || '').trim()

  const [status, setStatus] = useState<WizardStatus>('DRAFT')
  const [error, setError] = useState<string | null>(null)
  const [providerHint, setProviderHint] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [result, setResult] = useState<IssueWizardResult | null>(null)
  // Idempotency key — created lazily, once, at first issue (not during render).
  const idemRef = useRef<string | null>(null)
  // Double-submit guard beyond the disabled button (survives fast re-clicks).
  const inFlight = useRef(false)

  async function onIssue(form: WizardForm) {
    if (inFlight.current) return
    if (!projectId) {
      setStatus('FAILED')
      setError('Open this wizard from a project to issue Olivium access — no project is selected.')
      return
    }
    inFlight.current = true
    idemRef.current = idemRef.current ?? cryptoRandomId()
    setStatus('ISSUING'); setError(null); setProviderHint(null); setAuthRequired(false); setForbidden(false)
    try {
      const payload = buildCreatePayload(form, projectId, idemRef.current)
      const res = await api.access.create(payload)
      // Show ONLY authentic returned values — never fabricated.
      setResult({
        live: res.live === true,
        dryRun: res.dry_run === true,
        inviteId: res.invite?.id ?? '',
        status: res.invite?.status ?? 'pending',
        accessCode: res.access_code ?? null,
        message: res.message,
      })
      setStatus('ISSUED')
    } catch (err) {
      if (isAuthError(err)) { setAuthRequired(true); setStatus('DRAFT') }
      else if (isForbiddenError(err)) { setForbidden(true); setStatus('DRAFT') }
      else if (err instanceof ApiError && PROVIDER_REQUIRED_CODES.has(err.code)) {
        setStatus('PROVIDER_REQUIRED')
        setProviderHint('The Olivium engine isn’t connected yet. A live invitation can’t be issued until an operator wires it. Nothing was created — try a dry-run preview instead.')
      } else {
        setStatus('FAILED')
        setError((err as { message?: string })?.message || 'Could not reach the Olivium issuance service.')
      }
    } finally {
      inFlight.current = false
    }
  }

  return (
    <IssueSecureAccessWizard
      status={status}
      error={error}
      providerHint={providerHint}
      authRequired={authRequired}
      forbidden={forbidden}
      result={result}
      onHome={() => navigate(V2_ROOT)}
      onBackCommandCenter={() => navigate(V2_ROOT)}
      onOpenHub={() => navigate(SECURE_ACCESS_ROOT)}
      onIssue={onIssue}
    />
  )
}

// Small id helper — uses the platform UUID when available, else a random fallback.
// Never a secret; only an idempotency correlation value.
function cryptoRandomId(): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
    if (c?.randomUUID) return c.randomUUID()
  } catch { /* fall through */ }
  return `idem-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`
}
